// Dependency-free sanitizer for rich HTML that will be rendered via v-html.
// Content in this project is authored by trusted admins through the CMS editor, so
// this is a defense-in-depth pass — it strips active-content vectors (scripts, event
// handlers, javascript: URLs, embedded frames) while preserving standard formatting
// (headings, lists, links, images, tables, inline styles).
//
// Uses the browser DOMParser, so it must run client-side (it is only invoked from
// component render paths). For non-string input it returns an empty string.

const FORBIDDEN_TAGS = new Set([
  'SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'LINK', 'META', 'BASE', 'FORM', 'NOSCRIPT',
])

const URL_ATTRIBUTES = ['href', 'src', 'xlink:href']
const SAFE_URL_PATTERN = /^(https?:|mailto:|tel:|#|\/|\.\/|\.\.\/)/i
// Allow inline images (data:image/...) but nothing else via data: URIs.
const SAFE_DATA_IMAGE_PATTERN = /^data:image\/(png|jpe?g|gif|webp|svg\+xml);/i

function isSafeUrl(value) {
  const trimmed = String(value || '').trim()
  if (!trimmed) return true
  if (SAFE_DATA_IMAGE_PATTERN.test(trimmed)) return true
  return SAFE_URL_PATTERN.test(trimmed)
}

export function sanitizeHtml(dirty) {
  if (typeof dirty !== 'string' || dirty.trim() === '') return ''
  if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
    // No DOM available (SSR/tests): fail closed by dropping all markup.
    return dirty.replace(/<[^>]*>/g, '')
  }

  const doc = new DOMParser().parseFromString(dirty, 'text/html')
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_ELEMENT)
  const toRemove = []

  let node = walker.nextNode()
  while (node) {
    if (FORBIDDEN_TAGS.has(node.tagName)) {
      toRemove.push(node)
    } else {
      for (const attr of Array.from(node.attributes)) {
        const name = attr.name.toLowerCase()
        // Strip inline event handlers (onclick, onerror, …).
        if (name.startsWith('on')) {
          node.removeAttribute(attr.name)
          continue
        }
        // Strip unsafe URL protocols (javascript:, non-image data:, etc.).
        if (URL_ATTRIBUTES.includes(name) && !isSafeUrl(attr.value)) {
          node.removeAttribute(attr.name)
        }
      }
    }
    node = walker.nextNode()
  }

  toRemove.forEach(el => el.remove())
  return doc.body.innerHTML
}

// Extracts the plain text from an HTML string WITHOUT touching the live document.
// DOMParser produces an inert document — it does not load resources or run inline
// handlers — so this is safe on untrusted/CMS HTML, unlike assigning to the
// innerHTML of a detached element (which still fires `<img onerror>` and starts
// network requests). Returns '' for empty/non-string input.
// Preserves spacing after block elements (p, li, br, headings, div) for readability.
export function htmlToPlainText(html) {
  if (typeof html !== 'string' || html.trim() === '') return ''
  if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
    // No DOM available (SSR/tests): strip tags textually with spacing.
    return html
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<\/li>/gi, ' ')
      .replace(/<\/p>/gi, ' ')
      .replace(/<\/h[1-6]>/gi, ' ')
      .replace(/<\/div>/gi, ' ')
      .replace(/<[^>]*>/g, '')
      .replace(/\s+/g, ' ')
      .trim()
  }
  const doc = new DOMParser().parseFromString(html, 'text/html')
  
  // Add space after block elements before extracting text
  const blockElements = doc.querySelectorAll('br, p, li, h1, h2, h3, h4, h5, h6, div')
  blockElements.forEach(el => {
    el.insertAdjacentText('afterend', ' ')
  })
  
  return (doc.body.textContent ?? '')
    .replace(/\s+/g, ' ')
    .trim()
}
